import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/4jNChWMubsUVoT4hzpEWU9/Asset_12x.png';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState<'en' | 'fr'>('en');
  const location = useLocation();
  // Active nav helpers
  const isModelsActive = location.pathname.startsWith('/models');
  const isDealerActive = location.pathname === '/dealer-network';
  const isDifferenceActive = location.pathname.startsWith('/robeta-difference');
  const isDiscoverActive = ['/about-robeta', '/moments-on-the-map', '/tools', '/blog'].includes(location.pathname) || location.pathname.startsWith('/blog/');
  // Pages with full-screen dark hero images get transparent header
  const darkHeroPages = ['/', '/models/mythos4two', '/about-robeta', '/robeta-difference/silent-travel', '/robeta-difference/air-furniture', '/robeta-difference/premium-interiors', '/robeta-difference/customization', '/robeta-difference/signature-craftsmanship'];
  const hasDarkHero = darkHeroPages.includes(location.pathname);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Force solid header on pages without dark heroes
  const showSolid = !hasDarkHero || isScrolled;
  const headerClasses = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${showSolid ? 'bg-white shadow-sm py-4' : 'bg-transparent py-6'}`;
  const textClasses = showSolid ? 'text-robeta-black' : 'text-white';
  const borderClasses = showSolid ? 'border-robeta-black text-robeta-black' : 'border-white text-white';
  const solidButtonClasses = showSolid ? 'bg-robeta-black text-white hover:bg-robeta-copper' : 'bg-white text-robeta-black hover:bg-robeta-mist';
  const logoFilterClass = showSolid ? '' : 'brightness-0 invert';
  return <>
      <header className={headerClasses} data-id="element-149">
        <div className="container mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-150">
          {/* Logo */}
          <Link to="/" className="z-50 relative block" data-id="element-151">
            <img src={LOGO_URL} alt="Robeta" className={`h-8 md:h-10 w-auto transition-all duration-300 ${logoFilterClass}`} data-id="element-152" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-8" data-id="element-153">
            <div className="relative group" data-id="element-154">
              <Link to="/models" className={`flex items-center text-sm font-medium uppercase tracking-widest hover:text-robeta-copper transition-colors ${isModelsActive ? 'text-robeta-copper' : textClasses}`} data-id="element-155">
                Models <ChevronDown className="ml-1 w-4 h-4" data-id="element-156" />
              </Link>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" data-id="element-157">
                <div className="bg-white p-4 shadow-xl min-w-[200px] border-t-2 border-robeta-copper" data-id="element-158">
                  <Link to="/models/mythos4two" className={`block py-2 text-sm uppercase tracking-wide ${location.pathname === '/models/mythos4two' ? 'text-robeta-copper font-bold' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-159">
                    Mythos 4 Two
                  </Link>
                </div>
              </div>
            </div>

            <Link to="/dealer-network" className={`text-sm font-medium uppercase tracking-widest hover:text-robeta-copper transition-colors ${isDealerActive ? 'text-robeta-copper' : textClasses}`} data-id="element-160">
              Dealer Network
            </Link>

            <div className="relative group" data-id="element-161">
              <Link to="/robeta-difference" className={`flex items-center text-sm font-medium uppercase tracking-widest hover:text-robeta-copper transition-colors ${isDifferenceActive ? 'text-robeta-copper' : textClasses}`} data-id="element-162">
                Robeta Difference <ChevronDown className="ml-1 w-4 h-4" data-id="element-163" />
              </Link>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" data-id="element-164">
                <div className="bg-white p-4 shadow-xl min-w-[240px] border-t-2 border-robeta-copper" data-id="element-165">
                  <Link to="/robeta-difference/silent-travel" className={`block py-2 text-sm uppercase tracking-wide ${location.pathname === '/robeta-difference/silent-travel' ? 'text-robeta-copper font-bold' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-166">
                    Silent Travel
                  </Link>
                  <Link to="/robeta-difference/air-furniture" className={`block py-2 text-sm uppercase tracking-wide ${location.pathname === '/robeta-difference/air-furniture' ? 'text-robeta-copper font-bold' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-167">
                    Air Furniture
                  </Link>
                  <Link to="/robeta-difference/premium-interiors" className={`block py-2 text-sm uppercase tracking-wide ${location.pathname === '/robeta-difference/premium-interiors' ? 'text-robeta-copper font-bold' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-168">
                    Premium Interiors
                  </Link>
                  <Link to="/robeta-difference/customization" className={`block py-2 text-sm uppercase tracking-wide ${location.pathname === '/robeta-difference/customization' ? 'text-robeta-copper font-bold' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-169">
                    Customization
                  </Link>
                  <Link to="/robeta-difference/signature-craftsmanship" className={`block py-2 text-sm uppercase tracking-wide ${location.pathname === '/robeta-difference/signature-craftsmanship' ? 'text-robeta-copper font-bold' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-170">
                    Craftsmanship
                  </Link>
                </div>
              </div>
            </div>

            <div className="relative group" data-id="element-171">
              <button className={`flex items-center text-sm font-medium uppercase tracking-widest hover:text-robeta-copper transition-colors ${isDiscoverActive ? 'text-robeta-copper' : textClasses}`} data-id="element-172">
                Discover <ChevronDown className="ml-1 w-4 h-4" data-id="element-173" />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" data-id="element-174">
                <div className="bg-white p-4 shadow-xl min-w-[220px] border-t-2 border-robeta-copper" data-id="element-175">
                  <Link to="/about-robeta" className={`block py-2 text-sm uppercase tracking-wide ${location.pathname === '/about-robeta' ? 'text-robeta-copper font-bold' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-176">
                    About Us
                  </Link>
                  <Link to="/moments-on-the-map" className={`block py-2 text-sm uppercase tracking-wide ${location.pathname === '/moments-on-the-map' ? 'text-robeta-copper font-bold' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-177">
                    Moments on the Map
                  </Link>
                  <Link to="/tools" className={`block py-2 text-sm uppercase tracking-wide ${location.pathname === '/tools' ? 'text-robeta-copper font-bold' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-178">
                    Tools
                  </Link>
                  <Link to="/blog" className={`block py-2 text-sm uppercase tracking-wide ${location.pathname.startsWith('/blog') ? 'text-robeta-copper font-bold' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-179">
                    Journal
                  </Link>
                </div>
              </div>
            </div>
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center space-x-4" data-id="element-180">
            {/* Language Picker */}
            <div className="relative group" data-id="element-181">
              <button className={`flex items-center text-sm font-medium uppercase tracking-widest hover:text-robeta-copper transition-colors ${textClasses}`} data-id="element-182">
                <Globe className="w-4 h-4 mr-1.5" data-id="element-183" />
                {currentLang === 'en' ? 'EN' : 'FR'}
                <ChevronDown className="ml-1 w-3 h-3" data-id="element-184" />
              </button>
              <div className="absolute top-full right-0 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" data-id="element-185">
                <div className="bg-white p-3 shadow-xl min-w-[140px] border-t-2 border-robeta-copper" data-id="element-186">
                  <button onClick={() => setCurrentLang('en')} className={`w-full text-left block py-2 px-1 text-sm uppercase tracking-wide transition-colors ${currentLang === 'en' ? 'text-robeta-copper font-medium' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-187">
                    English
                  </button>
                  <button onClick={() => setCurrentLang('fr')} className={`w-full text-left block py-2 px-1 text-sm uppercase tracking-wide transition-colors ${currentLang === 'fr' ? 'text-robeta-copper font-medium' : 'text-robeta-black hover:text-robeta-copper'}`} data-id="element-188">
                    Français
                  </button>
                </div>
              </div>
            </div>

            <div className="w-px h-5 bg-current opacity-20" data-id="element-189" />

            <Link to="/contact" className={`px-6 py-2 border text-xs font-bold uppercase tracking-widest transition-colors hover:bg-white/10 ${borderClasses}`} data-id="element-190">
              Request Info
            </Link>
            <Link to="/dealer-network" className={`px-6 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${solidButtonClasses}`} data-id="element-191">
              Find a Dealer
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button className={`md:hidden z-50 relative ${isMobileMenuOpen ? 'text-robeta-black' : textClasses}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-192">
            {isMobileMenuOpen ? <X className="w-8 h-8" data-id="element-193" /> : <Menu className="w-8 h-8" data-id="element-194" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-195">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed inset-0 bg-white z-40 flex flex-col pt-32 px-6 pb-12 overflow-y-auto" data-id="element-196">
            <nav className="flex flex-col space-y-8" data-id="element-197">
              <div className="space-y-4" data-id="element-198">
                <h3 className="text-xs font-bold text-robeta-copper uppercase tracking-wide-editorial" data-id="element-199">
                  Model
                </h3>
                <Link to="/models/mythos4two" className="block text-3xl font-light text-robeta-black uppercase tracking-tight" data-id="element-200">
                  Mythos 4 Two
                </Link>
              </div>

              <div className="w-full h-px bg-gray-100" data-id="element-201" />

              <Link to="/dealer-network" className={`block text-3xl font-light uppercase tracking-tight ${isDealerActive ? 'text-robeta-copper' : 'text-robeta-black'}`} data-id="element-202">
                Dealer Network
              </Link>

              <div className="w-full h-px bg-gray-100" data-id="element-203" />

              <div className="space-y-4" data-id="element-204">
                <h3 className="text-xs font-bold text-robeta-copper uppercase tracking-wide-editorial" data-id="element-205">
                  Robeta Difference
                </h3>
                <Link to="/robeta-difference" className={`block text-3xl font-light uppercase tracking-tight ${location.pathname === '/robeta-difference' ? 'text-robeta-copper' : 'text-robeta-black'}`} data-id="element-206">
                  Overview
                </Link>
                <Link to="/robeta-difference/silent-travel" className={`block text-xl ${location.pathname === '/robeta-difference/silent-travel' ? 'text-robeta-copper font-medium' : 'text-robeta-black/70'}`} data-id="element-207">
                  Silent Travel
                </Link>
                <Link to="/robeta-difference/air-furniture" className={`block text-xl ${location.pathname === '/robeta-difference/air-furniture' ? 'text-robeta-copper font-medium' : 'text-robeta-black/70'}`} data-id="element-208">
                  Air Furniture Technology™
                </Link>
                <Link to="/robeta-difference/premium-interiors" className={`block text-xl ${location.pathname === '/robeta-difference/premium-interiors' ? 'text-robeta-copper font-medium' : 'text-robeta-black/70'}`} data-id="element-209">
                  Premium Interiors
                </Link>
                <Link to="/robeta-difference/customization" className={`block text-xl ${location.pathname === '/robeta-difference/customization' ? 'text-robeta-copper font-medium' : 'text-robeta-black/70'}`} data-id="element-210">
                  16,000 Customization Options
                </Link>
                <Link to="/robeta-difference/signature-craftsmanship" className={`block text-xl ${location.pathname === '/robeta-difference/signature-craftsmanship' ? 'text-robeta-copper font-medium' : 'text-robeta-black/70'}`} data-id="element-211">
                  Signature Craftsmanship
                </Link>
              </div>

              <div className="w-full h-px bg-gray-100" data-id="element-212" />

              <div className="space-y-4" data-id="element-213">
                <h3 className="text-xs font-bold text-robeta-copper uppercase tracking-wide-editorial" data-id="element-214">
                  Discover
                </h3>
                <Link to="/about-robeta" className={`block text-xl ${location.pathname === '/about-robeta' ? 'text-robeta-copper font-medium' : 'text-robeta-black/70'}`} data-id="element-215">
                  About Us
                </Link>
                <Link to="/moments-on-the-map" className={`block text-xl ${location.pathname === '/moments-on-the-map' ? 'text-robeta-copper font-medium' : 'text-robeta-black/70'}`} data-id="element-216">
                  Moments on the Map
                </Link>
                <Link to="/tools" className={`block text-xl ${location.pathname === '/tools' ? 'text-robeta-copper font-medium' : 'text-robeta-black/70'}`} data-id="element-217">
                  Tools
                </Link>
                <Link to="/blog" className={`block text-xl ${location.pathname.startsWith('/blog') ? 'text-robeta-copper font-medium' : 'text-robeta-black/70'}`} data-id="element-218">
                  Journal
                </Link>
              </div>

              <div className="mt-auto pt-8 space-y-4" data-id="element-219">
                {/* Mobile Language Picker */}
                <div className="flex items-center justify-center space-x-4 pb-4" data-id="element-220">
                  <Globe className="w-4 h-4 text-robeta-copper" data-id="element-221" />
                  <button onClick={() => setCurrentLang('en')} className={`text-sm uppercase tracking-widest font-bold transition-colors ${currentLang === 'en' ? 'text-robeta-copper' : 'text-robeta-black/40'}`} data-id="element-222">
                    EN
                  </button>
                  <span className="text-robeta-black/20" data-id="element-223">|</span>
                  <button onClick={() => setCurrentLang('fr')} className={`text-sm uppercase tracking-widest font-bold transition-colors ${currentLang === 'fr' ? 'text-robeta-copper' : 'text-robeta-black/40'}`} data-id="element-224">
                    FR
                  </button>
                </div>

                <Link to="/dealer-network" className="block w-full py-4 bg-robeta-black text-white text-center uppercase tracking-widest font-bold" data-id="element-225">
                  Find a Dealer
                </Link>
                <Link to="/contact" className="block w-full py-4 border border-robeta-black text-robeta-black text-center uppercase tracking-widest font-bold" data-id="element-226">
                  Request Info
                </Link>
              </div>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </>;
}